import React, { useState } from "react";
import { Backdrop, Box, Chip, Divider, IconButton, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { deleteDocument } from "../../firebase/firebaseCrud";
import ActionModal from "./ActionModal";
import { formatAmount } from "../../utils/utils";

function ProductModal({ product, onClose }) {
  const stockLow = typeof product.stock === "number" && product.stock <= 5;
  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, alignItems: "flex-start", pt: { xs: 4, md: 8 } }}
    >
      <Paper
        onClick={(e) => e.stopPropagation()}
        elevation={4}
        sx={{ width: { xs: "92vw", sm: 440 }, maxHeight: "80vh", overflowY: "auto", borderRadius: "16px", p: 0, outline: "none" }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 3, py: 2, borderBottom: "1px solid #e5e7eb",
            position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1, borderRadius: "16px 16px 0 0",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Product Details</Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Image */}
          {product.imgSrc && (
            <Box
              component="img"
              src={product.imgSrc}
              alt={product.title}
              sx={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: "10px", border: "1px solid #e5e7eb", mb: 2 }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}

          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#111827", mb: 0.5 }}>
            {product.title}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#194049", mb: 2 }}>
            {formatAmount(product.price)}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Details */}
          {[
            { label: "Stock", value: product.stock ?? "—", extra: stockLow ? " (Low)" : "" },
            { label: "Categories", value: Array.isArray(product.categories) ? product.categories.join(", ") : (product.categories || "—") },
          ].map(({ label, value, extra }) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
              <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 13, color: stockLow && label === "Stock" ? "#ef4444" : "#111827", fontWeight: label === "Stock" && stockLow ? 600 : 400 }}>
                {value}{extra}
              </Typography>
            </Box>
          ))}

          {product.description && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
                Description
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                {product.description}
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Backdrop>
  );
}

export const DashboardMediaCard = ({ id, imgSrc, title, price, slug, stock, categories, description, onDeleted }) => {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [showProduct, setShowProduct] = useState(false);

  const handleDelete = async (e) => {
    e.preventDefault();
    await deleteDocument("product-list", id);
    toast(`"${title}" deleted`, { type: "success" });
    setShowConfirm(false);
    onDeleted?.(id);
  };

  const stockLow = typeof stock === "number" && stock <= 5;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          borderBottom: "1px solid #f3f4f6",
          "&:last-child": { borderBottom: "none" },
          "&:hover": { bgcolor: "#fafafa" },
          transition: "background 0.15s",
        }}
      >
        {/* Image */}
        <Box
          component="img"
          src={imgSrc}
          alt={title}
          sx={{
            width: 56,
            height: 56,
            objectFit: "cover",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            flexShrink: 0,
            bgcolor: "#f3f4f6",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#111827",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
            {categories && (
              <Chip
                label={Array.isArray(categories) ? categories.join(", ") : categories}
                size="small"
                sx={{ fontSize: 10, height: 18, bgcolor: "#f3f4f6", color: "#6b7280" }}
              />
            )}
            <Typography sx={{ fontSize: 12, color: stockLow ? "#ef4444" : "#6b7280" }}>
              Stock: <strong>{stock ?? "—"}</strong>
              {stockLow && " · Low"}
            </Typography>
          </Box>
        </Box>

        {/* Price */}
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            flexShrink: 0,
            minWidth: 64,
            textAlign: "right",
          }}
        >
          {formatAmount(price)}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Box
            onClick={() => setShowProduct(true)}
            sx={{
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "6px", cursor: "pointer", color: "#6b7280",
              "&:hover": { bgcolor: "#ede9fe", color: "#7c3aed" }, transition: "all 0.15s",
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box
            onClick={() => navigate(`/dashboard/admin/update/${slug}/${id}`)}
            sx={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              cursor: "pointer",
              color: "#6b7280",
              "&:hover": { bgcolor: "#e0f2f1", color: "#194049" },
              transition: "all 0.15s",
            }}
          >
            <EditIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box
            onClick={() => setShowConfirm(true)}
            sx={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              cursor: "pointer",
              color: "#6b7280",
              "&:hover": { bgcolor: "#fee2e2", color: "#ef4444" },
              transition: "all 0.15s",
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </Box>
        </Box>
      </Box>

      {showConfirm && (
        <ActionModal
          statement={`Delete "${title}"?`}
          label1="Delete"
          label2="Cancel"
          modalstate={showConfirm}
          closeModal={() => setShowConfirm(false)}
          handleDelete={handleDelete}
        />
      )}

      {showProduct && (
        <ProductModal
          product={{ id, imgSrc, title, price, slug, stock, categories, description }}
          onClose={() => setShowProduct(false)}
        />
      )}
    </>
  );
};
